/**
 * Orchestration de l'éditeur avancé d'exercice : rôles, données, auto-save
 * et handlers (extrait de ExerciceAdvancedEditor).
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useRBAC } from '../../../contexts/RBACContext';
import { useEnvironment } from '../../../contexts/EnvironmentContext';
import { useNotify } from '../../../contexts/NotificationContext';
import { useExerciceLoader } from './useExerciceLoader';
import { useEditorAutoSave } from './useEditorSession';
import { useEditorHandlers, useExportForAI } from './useEditorHandlers';

/** Tout l'état et les handlers de l'éditeur avancé d'exercice. */
export function useExerciceAdvancedEditor() {
  const { environment } = useEnvironment();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin, canUseAICorrection } = useRBAC();

  // Mode lecture seule, forcé via l'adresse (?readOnly=true)
  const isReadOnly = searchParams.get('readOnly') === 'true';

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const { notify } = useNotify('exerciseEditor');
  const data = useExerciceLoader(id, environment, isAdmin, notify);
  const autoSave = useEditorAutoSave({ id, environment, isReadOnly });

  const { loadExercice } = data;
  useEffect(() => {
    loadExercice();
  }, [loadExercice]);

  const handlers = useEditorHandlers({
    id, environment, navigate, notify,
    setError: data.setError, setPreviewData: data.setPreviewData, loadExercice,
  });
  const handleExportForAI = useExportForAI({ ...data, notify });

  return {
    ...data, ...autoSave, ...handlers, navigate, notify, isAdmin, canUseAICorrection, isReadOnly,
    importDialogOpen, setImportDialogOpen, showPreview, setShowPreview, handleExportForAI,
    // URL de retour pour l'éditeur LaTeX
    returnPath: `/exercices/${id}/edit-advanced`,
  };
}

/** Valeur renvoyée par useExerciceAdvancedEditor. */
export type ExerciceAdvancedEditorState = ReturnType<typeof useExerciceAdvancedEditor>;
